import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, BedDouble, Ruler, CalendarClock, Check, Building2, ChevronLeft, ChevronRight } from "lucide-react";
import { CONTACT } from "../data/site";
import { LeadForm } from "../components/LeadForm";
import { Reveal } from "../components/shared";
import { Seo, SITE_URL, breadcrumbJsonLd } from "../components/Seo";
import { useI18n } from "../i18n/LanguageContext";
import { useProjects } from "../hooks/useProjects";

const Spec = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-navy/8 bg-white p-5">
    <Icon size={20} className="text-gold" />
    <p className="mt-3 text-xs uppercase tracking-wide text-muted">{label}</p>
    <p className="mt-0.5 font-display text-lg font-semibold text-navy">{value || "—"}</p>
  </div>
);

export default function ProjectDetail() {
  const { id } = useParams();
  const { t, c, lang } = useI18n();
  const d = t.detail;
  const { projects: list, loading } = useProjects(c.PROJECTS);
  const project = list.find((p) => p.id === id);
  const [active, setActive] = useState(0);

  if (loading && !project) {
    return (
      <div className="container-x flex min-h-[70vh] flex-col items-center justify-center pt-28" data-testid="project-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold border-t-transparent" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container-x flex min-h-[70vh] flex-col items-center justify-center text-center pt-28" data-testid="project-not-found">
        <h1 className="font-display text-4xl font-semibold text-navy">{d.notFound}</h1>
        <Link to="/projects" className="btn-primary mt-6">{d.backBtn}</Link>
      </div>
    );
  }

  const gallery = project.gallery?.length ? project.gallery : (project.image ? [project.image] : []);

  // Parse a display price like "From AED 1.96M" / "550K" into a clean number for structured data
  const priceNum = (() => {
    if (!project.price) return null;
    const m = String(project.price).replace(/,/g, "").match(/([\d.]+)\s*([mMkK])?/);
    if (!m) return null;
    let n = parseFloat(m[1]);
    const suf = (m[2] || "").toLowerCase();
    if (suf === "m") n *= 1e6;
    else if (suf === "k") n *= 1e3;
    return Math.round(n);
  })();

  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: project.name,
    image: gallery,
    description: project.description,
    brand: { "@type": "Brand", name: project.developer || "OffplanDubai.nl" },
    ...(priceNum
      ? { offers: { "@type": "Offer", priceCurrency: project.currency || "AED", price: priceNum, availability: "https://schema.org/InStock", url: `${SITE_URL}/projects/${project.id}` } }
      : {}),
  };

  const seoTitle = `${project.name} — Off-Plan${project.location ? ` in ${project.location}` : " Dubai"}`;
  const seoDesc = [
    `${project.name}${project.developer ? ` by ${project.developer}` : ""} — off-plan${project.location ? ` in ${project.location}` : " Dubai"}.`,
    project.bedrooms ? `${project.bedrooms} bedroom units.` : "",
    project.price ? `From ${project.price.replace(/^From\s*/i, "")}.` : "",
    project.handover ? `Handover ${project.handover}.` : "",
    "Zero buyer commission, flexible payment plans.",
  ].filter(Boolean).join(" ").slice(0, 160);

  // Clean up imported copy: decode HTML entities, fix mojibake, collapse whitespace
  const decodeEntities = (s) => {
    if (!s || typeof document === "undefined") return s || "";
    const el = document.createElement("textarea");
    el.innerHTML = s;
    return el.value;
  };
  const cleanText = (s) => decodeEntities(s || "")
    .replace(/\u00e2\u20ac\u201c|\u00e2\u20ac\u201d/g, "–")
    .replace(/\u00e2\u20ac\u2122/g, "’")
    .replace(/\u00c2/g, "")
    .replace(/[ \t]+/g, " ")
    .trim();
  // Dutch content when available in NL mode, else the English source
  const localizedDesc = (lang === "nl" && project.description_nl) ? project.description_nl : project.description;
  const localizedHighlights = (lang === "nl" && project.highlights_nl?.length) ? project.highlights_nl : project.highlights;
  const descLines = cleanText(localizedDesc).replace(/\r/g, "").split("\n").map((s) => s.trim()).filter(Boolean);
  // hide highlights that just repeat the description lines (imported projects)
  const extraHighlights = (localizedHighlights || []).map(cleanText).filter((h) => !descLines.includes(h));

  return (
    <div className="pt-28 sm:pt-32" data-testid="project-detail">
      <Seo path={`/projects/${project.id}`} title={seoTitle}
        description={seoDesc} image={project.image} type="article"
        jsonLd={[productLd, breadcrumbJsonLd([{ name: t.crumbHome, path: "/" }, { name: t.projectsPage.crumb, path: "/projects" }, { name: project.name, path: `/projects/${project.id}` }])]} />
      <div className="container-x">
        <Link to="/projects" data-testid="detail-back" className="inline-flex items-center gap-2 text-sm font-medium text-muted transition-colors hover:text-gold"><ArrowLeft size={16} /> {d.back}</Link>

        <div className="mt-6 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <span className="eyebrow">{project.tag}</span>
            <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">{project.name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-muted">
              <span className="flex items-center gap-1.5"><MapPin size={16} className="text-gold" /> {project.location}</span>
              <span className="flex items-center gap-1.5"><Building2 size={16} className="text-gold" /> {project.developer}</span>
            </div>
          </div>
          <div className="rounded-2xl bg-navy px-6 py-4 text-white">
            <span className="text-xs uppercase tracking-wide text-white/60">{d.from}</span>
            <p className="font-display text-2xl font-semibold text-gold-300">{project.price ? `${project.price}${String(project.price).includes(project.currency) ? "" : ` ${project.currency}`}` : d.priceOnRequest || "Price on request"}</p>
          </div>
        </div>

        <div className="mt-8 grid gap-3 lg:grid-cols-4">
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative overflow-hidden rounded-3xl lg:col-span-3">
            {gallery[active] || project.image ? (
              <img src={gallery[active] || project.image} alt={project.name} className="h-[300px] w-full object-cover sm:h-[460px]" />
            ) : (
              <div className="flex h-[300px] w-full items-center justify-center bg-navy/5 sm:h-[460px]"><Building2 size={48} className="text-navy/20" /></div>
            )}
            {gallery.length > 1 && (
              <>
                <button onClick={() => setActive((active - 1 + gallery.length) % gallery.length)} data-testid="gallery-prev" aria-label="Vorige foto"
                  className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-navy shadow-lg backdrop-blur transition hover:bg-white">
                  <ChevronLeft size={22} /></button>
                <button onClick={() => setActive((active + 1) % gallery.length)} data-testid="gallery-next" aria-label="Volgende foto"
                  className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-navy shadow-lg backdrop-blur transition hover:bg-white">
                  <ChevronRight size={22} /></button>
                <span className="absolute bottom-3 right-3 rounded-full bg-navy/70 px-3 py-1 text-xs font-medium text-white backdrop-blur" data-testid="gallery-counter">{active + 1} / {gallery.length}</span>
              </>
            )}
          </motion.div>
          <div className="grid grid-cols-4 gap-3 lg:grid-cols-1 lg:max-h-[460px] lg:content-start lg:overflow-y-auto lg:pr-1">
            {gallery.map((g, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
                className={`overflow-hidden rounded-2xl transition-[box-shadow,opacity] duration-300 ${active === i ? "ring-2 ring-gold" : "opacity-70 hover:opacity-100"}`}>
                <img src={g} alt="" loading="lazy" className="h-20 w-full object-cover lg:h-[105px]" />
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12 grid gap-10 pb-24 lg:grid-cols-[1.6fr_1fr]">
          <div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Spec icon={BedDouble} label={d.bedrooms} value={project.bedrooms} />
              <Spec icon={Ruler} label={d.size} value={project.size} />
              <Spec icon={CalendarClock} label={d.handover} value={project.handover} />
              <Spec icon={Building2} label={d.developer} value={project.developer} />
            </div>
            <Reveal><h2 className="mt-12 font-display text-2xl font-semibold text-navy">{d.about}</h2>
              <div className="mt-4 space-y-3">
                {descLines.map((line, i) => {
                  const parts = line.split(/\s+[–-]\s+/);
                  if (parts.length >= 2) {
                    const label = parts[0];
                    const rest = line.slice(label.length).replace(/^\s*[–-]\s*/, "");
                    return <p key={i} className="text-base leading-relaxed text-muted"><strong className="font-semibold text-navy">{label}</strong> — {rest}</p>;
                  }
                  return <p key={i} className="text-base leading-relaxed text-muted">{line}</p>;
                })}
              </div>
            </Reveal>
            {extraHighlights.length > 0 && (
              <Reveal>
                <h2 className="mt-10 font-display text-2xl font-semibold text-navy">{d.highlights}</h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {extraHighlights.map((h) => (
                    <div key={h} className="flex items-center gap-3 rounded-2xl bg-white p-4 shadow-soft">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold"><Check size={16} /></span>
                      <span className="text-sm font-medium text-navy">{h}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}
            {project.payment?.length > 0 && (
              <Reveal>
                <h2 className="mt-10 font-display text-2xl font-semibold text-navy">{d.payment}</h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {project.payment.map((p) => (
                    <div key={p.label} className="rounded-2xl border border-navy/8 bg-white p-5 text-center">
                      <p className="font-display text-3xl font-semibold text-gold">{p.value}</p>
                      <p className="mt-1 text-sm text-muted">{lang === "nl" ? ({ "On booking": "Bij reservering", "During construction": "Tijdens de bouw", "On handover": "Bij oplevering" }[p.label] || p.label) : p.label}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}
          </div>

          <div>
            <div className="lg:sticky lg:top-28">
              <div className="mb-4 rounded-3xl bg-navy p-6 text-white">
                <h3 className="font-display text-xl font-semibold">{d.interested} {project.name}?</h3>
                <p className="mt-2 text-sm text-white/70">{d.interestedSub}</p>
                <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" data-testid="detail-whatsapp" className="btn-wa mt-4 w-full">{d.whatsapp}</a>
              </div>
              <LeadForm interest={project.name} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
